import { v4 as uuidv4 } from 'uuid'
import { type ClientLog, type Extra, LoggableError, printLog, type Tags } from './ClientLog.no-deps'
import type { UUID } from './typeHelpers.no-deps'
import { type CatchError, ensureError } from './functional/error.no-deps'
import type { ConsoleType } from './console.no-deps'
import { always, doNothing } from './functional/function.no-deps'

type SaveLog = (log: ClientLog) => void
type ReportError = (error: Error, extra?: Extra, tags?: Tags) => void

export interface ClientLoggerOptions {
    logGroupName: string
    saveLog?: SaveLog
    reportError?: ReportError
    getHashPath?: () => string
}

export interface AbstractClientLogger {
    logGroupId: UUID
    logGroupName: string
    error(maybeError: CatchError, extra?: Extra, tags?: Tags): Error
    info(message: string, extra?: Extra, tags?: Tags): void
    log(message: string, extra?: Extra, tags?: Tags): void
    warn(message: string, extra?: Extra, tags?: Tags): void
}

export class ClientLogger implements AbstractClientLogger {
    private startTime = Date.now()
    public logGroupId = uuidv4() as UUID
    public logGroupName: string
    private saveLog: SaveLog
    private reportError: ReportError
    private getHashPath: () => string

    constructor(opts: ClientLoggerOptions) {
        this.logGroupName = opts.logGroupName
        this.saveLog = opts.saveLog ?? doNothing
        this.reportError = opts.reportError ?? doNothing
        this.getHashPath = opts.getHashPath ?? always(null)
        this.log('started')
    }

    public error = (maybeError: CatchError, extra?: Extra, tags?: Tags): Error => {
        try {
            const error = ensureError(maybeError)

            if (error instanceof LoggableError) {
                extra = { ...extra, ...error.extra }
                tags = { ...tags, ...error.tags }
            }

            const log = this.createLog('error', error.message, extra, tags)

            extra = { ...extra, log }
            tags = { ...tags, logGroupName: this.logGroupName }

            this.reportError(error, extra, tags)
            console.error(error, log) /* eslint-disable-line no-console */
            this.saveLog(log)

            return error
        } catch (newError) {
            this.reportError(newError, extra, tags)
            console.error(newError) /* eslint-disable-line no-console */
            return newError
        }
    }

    private message(logLevel: ConsoleType, message: string, extra?: Extra, tags?: Tags) {
        try {
            const log = this.createLog(logLevel, message, extra, tags)
            printLog(log)
            this.saveLog(log)
        } catch (error) {
            this.error(error)
        }
    }

    public info = (message: string, extra?: Extra, tags?: Tags) => this.message('info', message, extra, tags)
    public log = (message: string, extra?: Extra, tags?: Tags) => this.message('log', message, extra, tags)
    public warn = (message: string, extra?: Extra, tags?: Tags) => this.message('warning', message, extra, tags)

    private createLog(logLevel: ConsoleType, message: string, extra?: Extra, tags?: Tags): ClientLog {
        return {
            logLevel,
            logGroupId: this.logGroupId,
            logGroupName: this.logGroupName,
            timestamp: new Date(),
            timeDiff: Date.now() - this.startTime,
            message,
            extra,
            tags,
            hashpath: this.getHashPath(),
        }
    }
}

export class QuietClientLogger implements AbstractClientLogger {
    public logGroupId = uuidv4() as UUID
    public logGroupName = 'QuietClientLogger'
    public error = (maybeError: CatchError, _extra?: Extra, _tags?: Tags): Error => ensureError(maybeError)
    public info = (_message: string, _extra?: Extra, _tags?: Tags) => doNothing()
    public log = (_message: string, _extra?: Extra, _tags?: Tags) => doNothing()
    public warn = (_message: string, _extra?: Extra, _tags?: Tags) => doNothing()
}
